import React from 'react';
import {
  Edit,
  TabbedShowLayout,
  Tab,
  usePermissions,
  useNotify,
  useRefresh,
  ListBase,
  useRecordContext,
} from 'react-admin';

import {
  Card,
  SxProps,
} from '@mui/material';

import useCustomEditView from '../../hooks/useCustomEditView';

import CarOwnership from '../../customFields/CarOwnership';

import actions, { Actions } from '../../constants/actions';
import Information from './editTabs/Information';

import hasAccess from '../../utilities/hasAccess';

import KycForm from '../kycForm';
import BusinessBreadcrumb from './BusinessBreadcrumb';
import Transaction from '../transaction';
import Categorization from '../categorization';
import useValidateId from '../../hooks/useValidateId';
import resourceTypes from '../../constants/resourceTypes';
import { entityCleaner } from '../../utilities/dataProvider/cleaner';
import ReportModal from '../../customFields/ReportModal';
import reportTypes from '../../constants/reportTypes';
import KycDataTabs from './editTabs/kyc/KycDataTabs';
import { attributesSchema, checkIsTypeAllowed } from '../../utilities/reports/permissions';
import ConnectedProducts from '../product/ConnectedProducts';
import ConnectedAccounts from '../accountEntity/ConnectedAccounts';
import AuditResourceList from '../audit/AuditResourceList';
import EditToolbar from '../layout/EditToolbar';
import { ReportListDatagrid } from '../reports/ReportHistoryTable';

const customEditSx = (isCustomView: boolean): SxProps => (
  isCustomView
    ? { '& .RaEdit-card': { backgroundColor: 'transparent', boxShadow: 'none', marginTop: 0 } }
    : { '& .RaEdit-card': { marginTop: 0 } }
);

const EditActions = () => (
  <EditToolbar>
    <ReportModal />
  </EditToolbar>
);

export const BusinessEditContent = () => {
  const { permissions } = usePermissions<Actions[]>();
  const record = useRecordContext();

  const { isCustomView } = useCustomEditView();

  if (!record) return null;

  return (
    <TabbedShowLayout
      sx={isCustomView ? { display: 'none' } : {}}
    >
      <Tab label="Information">
        <Information />
      </Tab>
      <Tab label="KYC data" path="kyc-data">
        <KycDataTabs />
      </Tab>
      {hasAccess(permissions, actions.FORM_ANSWER_GET) && (
        <Tab label="KYC form" path="form-answers/*">
          <KycForm />
        </Tab>
      )}
      {hasAccess(permissions, actions.REQUEST_GET) && (
        <Tab label="Transactions" path="transactions">
          <Transaction />
        </Tab>
      )}
      {hasAccess(permissions, actions.CAR_GET) && (
        <Tab label="Car ownership" path="cars">
          <CarOwnership />
        </Tab>
      )}
      {hasAccess(permissions, actions.ACCOUNT_ENTITY_LIST) && (
        <Tab label="Connected accounts" path="accounts">
          <ConnectedAccounts />
        </Tab>
      )}
      {hasAccess(permissions, actions.PRODUCT_GET) && (
        <TabbedShowLayout.Tab label="Connected products" path="products">
          <ConnectedProducts />
        </TabbedShowLayout.Tab>
      )}
      {hasAccess(permissions, actions.BANK_DATA_GET_CATEGORIZATION) && (
        <Tab label="Categorization" path="categorization">
          <Categorization />
        </Tab>
      )}
      {hasAccess(permissions, actions.AUDIT_TRACK_LIST) && (
        <TabbedShowLayout.Tab label="Audit trail" path="audit-tracks">
          <AuditResourceList resourceType={resourceTypes.BUSINESS} />
        </TabbedShowLayout.Tab>
      )}
      {hasAccess(permissions, actions.REPORT_LIST, {
        schema: attributesSchema,
        check: checkIsTypeAllowed(reportTypes.ENTITY_REPORT),
      }) && (
        <Tab label="Reports" path="reports">
          <ListBase
            storeKey={false}
            disableSyncWithLocation
            perPage={5}
            resource="reports"
            sort={{ field: 'createdAt', order: 'DESC' }}
            filter={{ type: reportTypes.ENTITY_REPORT, entityId: record.id }}
            queryOptions={{ refetchInterval: 5000 }}
          >
            <Card variant="outlined">
              <ReportListDatagrid />
            </Card>
          </ListBase>
        </Tab>
      )}
    </TabbedShowLayout>
  );
};

const BusinessEdit = () => {
  const notify = useNotify();
  const refresh = useRefresh();

  const { isCustomView } = useCustomEditView();

  const onSuccess = () => {
    notify('Individual updated');
    refresh();
  };

  const validatedId = useValidateId();
  if (!validatedId.isValid) return null;

  return (
    <Edit
      mutationMode="pessimistic"
      mutationOptions={{ onSuccess }}
      sx={customEditSx(isCustomView)}
      transform={entityCleaner}
      actions={<EditActions />}
    >
      <BusinessBreadcrumb />
      <BusinessEditContent />
    </Edit>
  );
};

export default BusinessEdit;
